import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthHelperService } from './auth-helper.service';
import { TurnstileService } from './turnstile.service';
import { dispatchChatExchangeStarted } from '../utils/chat-scroll.helper';
import { CHAT_ERROR_MESSAGES, userFacingChatError } from '../utils/chat-error-messages';
import { detectWebViewContext } from '../utils/web-view-context';

export interface PromptSettings {
  settings: {
    id: number;
    video_prompt: string;
    document_prompt?: string;
    temperature: number;
    ConversationStarter1: string;
    ConversationStarter2: string;
    ConversationStarter3: string;
    MaxTokens: number;
    TopK: number;
    ResponseTokens: number;
    AIAvatar: string;
    UserAvatar: string;
  } | null;
  conversationStarters: string[];
  modelConfig: {
    maxTokens: number;
    topK: number;
    temperature: number;
  };
  avatars: {
    userAvatar: string;
    aiAvatar: string;
  };
}

export type ConversationMode = 'chat' | 'quiz';
export type QuizAction = 'start' | 'answer' | 'new_round';
export type QuizGrade = 'correct' | 'partial' | 'incorrect' | null;

/** Temporarily disabled for GPT-6 model rollout — set true to re-enable Bible Quiz. */
export const BIBLE_QUIZ_ENABLED = false;

export interface QuizState {
  questionNumber: number;
  correct: number;
  partial: number;
  incorrect: number;
  lastQuestion: string;
  lastTopic: string;
  previousTopics: string[];
  totalQuestions: number;
  roundComplete: boolean;
}

export interface QuizMessageDetails {
  phase?: string;
  questionNumber: number;
  totalQuestions: number;
  correct: number;
  partial?: number;
  incorrect: number;
  grade?: QuizGrade;
  feedback?: string;
  expectedAnswer?: string;
  verses?: string;
  question?: string;
  topic?: string;
  roundComplete?: boolean;
  /** Server asks client to append a second bubble with the next question */
  splitNextQuestion?: boolean;
  nextQuestionHtml?: string | null;
  /** True when this message is only the next-question card (no grading controls) */
  isQuestionCard?: boolean;
  /** UI: which detail panels are expanded */
  expanded?: {
    feedback?: boolean;
    verses?: boolean;
    sources?: boolean;
  };
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  question?: string;
  answer?: string;
  timestamp: Date;
  isProcessing?: boolean;
  sources?: any[];
  followupQuestions?: string[];
  chatId?: number; // Database chat ID for feedback
  /** Total segments stored in ChatLogs.ContextSegmentIds for this answer */
  contextSegmentCount?: number;
  /** Whether more sources can be loaded (server-driven after expand) */
  hasMoreSources?: boolean;
  feedback?: number; // 1 for thumbs up, 0 for thumbs down, undefined for no feedback
  /** Present on assistant messages when conversationMode is quiz */
  quiz?: QuizMessageDetails;
  metadata?: {
    confidence?: number;
    modelUsed?: string;
    sources?: any[];
  };
}

export interface ChatResponse {
  message: string;
  confidence: number;
  sources: any[];
  suggestedQuestions: string[];
  modelUsed: string;
  tokensUsed: number;
  sessionId: string;
  conversationLength: number;
  chatId?: number; // Added for feedback
  crisis?: {
    level: string;
    resources: CrisisResources;
  };
}

export interface CrisisResources {
  suicideLine: { label: string; phone: string; href: string };
  crisisTextLine: { label: string; phone: string; href: string };
  emergency: { label: string; phone: string; href: string };
  disclaimer: string;
}

export interface VideoSegment {
  id: string;
  title: string;
  teacher: string;
  content: string;
  sourceType: string;
  videoLink?: string;
  docLink?: string;
  tags: string[];
  score?: number;
  // Backend-generated URLs with timestamps
  videoUrl?: string;
  embedUrl?: string;
  previewUrl?: string;
      documentUrl?: string;
    // Backend-extracted timestamp from segment
    segmentTimestamp?: number;
}

export interface UserPreferences {
  preferredTeachers?: string[];
  preferredTopics?: string[];
  maxResults?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private apiUrl = environment.apiUrl;
  private sessionId = this.restoreOrCreateSessionId();
  private currentConversationId: string | null = this.restoreConversationId(); // Store the conversationId when resuming an old conversation
  private static readonly SESSION_STORAGE_KEY = 'askandy_sessionId';
  private static readonly CONVERSATION_STORAGE_KEY = 'askandy_conversationId';
  private authHelper = inject(AuthHelperService);
  private turnstileService = inject(TurnstileService);
  
  // Use signals for reactive state management
  public readonly chatHistory = signal<ChatMessage[]>([]);
  /** Set while POST /chat/more-sources is in flight for a given chat row */
  public readonly loadingMoreSourcesChatId = signal<number | null>(null);
  public readonly isProcessing = signal<boolean>(false);
  public readonly crisisBannerActive = signal<boolean>(false);
  public readonly crisisResources = signal<CrisisResources | null>(null);
  public readonly crisisLevel = signal<string | null>(null);
  public readonly conversationStarters = signal<string[]>([]);
  public readonly lastTopSegments = signal<VideoSegment[]>([]);
  public readonly promptSettings = signal<PromptSettings | null>(null);
  public readonly chatAvatars = signal<{ userAvatar: string; aiAvatar: string }>({
    userAvatar: '📖',
    aiAvatar: '⛪'
  });

  public readonly conversationMode = signal<ConversationMode>('chat');
  public readonly quizState = signal<QuizState>(ChatService.initialQuizState());
  
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$ = this.messagesSubject.asObservable();
  
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadChatHistory();
    this.loadPromptSettings(); // Load prompt settings first
    this.loadConversationStarters(); // Also load conversation starters
  }

  private static initialQuizState(): QuizState {
    return {
      questionNumber: 0,
      correct: 0,
      partial: 0,
      incorrect: 0,
      lastQuestion: '',
      lastTopic: '',
      previousTopics: [],
      totalQuestions: 7,
      roundComplete: false,
    };
  }

  isQuizMode(): boolean {
    return BIBLE_QUIZ_ENABLED && this.conversationMode() === 'quiz';
  }

  isBibleQuizEnabled(): boolean {
    return BIBLE_QUIZ_ENABLED;
  }

  async enterQuizMode(): Promise<void> {
    if (!BIBLE_QUIZ_ENABLED) {
      return;
    }
    // clearChat resets mode to chat; re-enter quiz mode afterward.
    await this.clearChat(false);
    this.conversationMode.set('quiz');
    this.quizState.set(ChatService.initialQuizState());
    // Ensure mode is quiz before the stream payload is built.
    if (!this.isQuizMode()) {
      this.conversationMode.set('quiz');
    }
    await this.processQuestion('Please begin the Bible quiz.', 'start');
  }

  async startQuizNewRound(): Promise<void> {
    if (!this.isQuizMode()) {
      await this.enterQuizMode();
      return;
    }
    const priorTopics = this.quizState().previousTopics;
    // Fresh on-screen round; keep prior topics so the professor avoids repeats.
    this.chatHistory.set([]);
    this.quizState.set({
      ...ChatService.initialQuizState(),
      previousTopics: priorTopics,
    });
    this.conversationMode.set('quiz');
    await this.processQuestion('Start a new 7-question Bible quiz round.', 'new_round');
  }

  async exitQuizMode(): Promise<void> {
    this.conversationMode.set('chat');
    this.quizState.set(ChatService.initialQuizState());
    await this.clearChat(false);
  }

  toggleQuizDetail(
    messageId: string,
    panel: 'feedback' | 'verses' | 'sources'
  ): void {
    this.chatHistory.update((history) =>
      history.map((msg) => {
        if (msg.id !== messageId || !msg.quiz) {
          return msg;
        }
        const expanded = { ...(msg.quiz.expanded || {}) };
        expanded[panel] = !expanded[panel];
        return { ...msg, quiz: { ...msg.quiz, expanded } };
      })
    );
  }

  private applyQuizCompleteMeta(meta: QuizMessageDetails | undefined): void {
    if (!meta) {
      return;
    }
    const prev = this.quizState();
    const topic = (meta.topic || '').trim();
    const previousTopics = topic
      ? [...prev.previousTopics.filter((t) => t !== topic), topic].slice(-12)
      : prev.previousTopics;

    this.quizState.set({
      questionNumber: Number(meta.questionNumber) || prev.questionNumber,
      correct: Number(meta.correct) || 0,
      partial: Number(meta.partial) || 0,
      incorrect: Number(meta.incorrect) || 0,
      lastQuestion: (meta.question || prev.lastQuestion || '').trim(),
      lastTopic: topic || prev.lastTopic,
      previousTopics,
      totalQuestions: Number(meta.totalQuestions) || 7,
      roundComplete: Boolean(meta.roundComplete),
    });
  }

  private generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private restoreOrCreateSessionId(): string {
    try {
      const saved = sessionStorage.getItem(ChatService.SESSION_STORAGE_KEY);
      if (saved) {
        return saved;
      }
    } catch {
      /* ignore */
    }
    const id = this.generateSessionId();
    this.persistSessionId(id);
    return id;
  }

  private persistSessionId(id: string): void {
    try {
      sessionStorage.setItem(ChatService.SESSION_STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
  }

  private restoreConversationId(): string | null {
    try {
      const saved = sessionStorage.getItem(ChatService.CONVERSATION_STORAGE_KEY);
      if (saved && saved !== 'undefined' && saved !== 'null') {
        return saved;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  // Add a simple message (for user questions)
  addMessage(question: string): void {
    const userMessage: ChatMessage = {
      id: this.generateMessageId(),
      role: 'user',
      question,
      timestamp: new Date()
    };
    
    const currentHistory = this.chatHistory();
    this.chatHistory.set([...currentHistory, userMessage]);
    
    // Add processing message
    const processingMessage: ChatMessage = {
      id: this.generateMessageId(),
      role: 'assistant',
      answer: '',
      timestamp: new Date(),
      isProcessing: true
    };
    
    this.chatHistory.set([...this.chatHistory(), processingMessage]);
  }

  // Process a question and get AI response
  async processQuestion(question: string, quizActionOverride?: QuizAction): Promise<void> {
    try {
      this.isProcessing.set(true);
      
      // Get user preferences from context
      const userPreferences: UserPreferences = {
        preferredTeachers: [],
        preferredTopics: [],
        maxResults: 10
      };
      
      // Use streaming by default for better user experience
      // sendMessageStream will handle adding both user message and AI response
      await this.sendMessageStream(
        question,
        userPreferences,
        (chunk: string) => {
          // onChunk - handle real-time streaming updates
          // The streaming is already handled in sendMessageStream by updating chatHistory
        },
        (sources) => {
          // onComplete - update lastTopSegments with sources
          if (sources && sources.length > 0) {
            this.lastTopSegments.set(sources);
          }
        },
        (error) => {
          // Error handled in onError callback
        },
        quizActionOverride
      );
      
    } catch (error) {
      // Add error message to chat
      const errorMessage: ChatMessage = {
        id: this.generateMessageId(),
        role: 'assistant',
        answer: userFacingChatError(CHAT_ERROR_MESSAGES.generic),
        timestamp: new Date(),
        metadata: {
          confidence: 0,
          modelUsed: 'error'
        }
      };
      
      // Add error message to chat history
      const currentHistory = this.chatHistory();
      this.chatHistory.set([...currentHistory, errorMessage]);
      
    } finally {
      this.isProcessing.set(false);
    }
  }

  // Ask a followup question
  async askFollowupQuestion(question: string): Promise<void> {
    // Don't call addMessage here as processQuestion will handle it
    await this.processQuestion(question);
  }

  async sendMessage(message: string, userPreferences?: UserPreferences): Promise<ChatResponse> {
    try {
      this.isLoadingSubject.next(true);
      
      // Add user message to local state
      const userMessage: ChatMessage = {
        id: this.generateMessageId(),
        role: 'user',
        question: message,
        timestamp: new Date()
      };
      
      this.addMessage(message);

      // Get auth token for Authorization header
      const authToken = this.authHelper.getToken();
      const headers: { [key: string]: string } = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const payload = await this.buildChatPayload(message, userPreferences);

      // Send to backend with Authorization header
      const response = await this.http.post<ChatResponse>(`${this.apiUrl}/chat`, payload, { headers }).toPromise();
      
      if (!response) {
        throw new Error('No response received from server');
      }

      // Crisis banner disabled pending leadership approval
      // if (response.crisis) {
      //   this.activateCrisisBanner(response.crisis);
      // }

      // Add AI response to local state
      const aiMessage: ChatMessage = {
        id: this.generateMessageId(),
        role: 'assistant',
        answer: response.message,
        timestamp: new Date(),
        sources: response.sources,
        followupQuestions: response.suggestedQuestions,
        chatId: response.chatId, // Include chat ID for feedback
        metadata: {
          confidence: response.confidence,
          modelUsed: response.modelUsed,
          sources: response.sources
        }
      };
      
      // Add AI message to chat history
      const currentHistory = this.chatHistory();
      this.chatHistory.set([...currentHistory, aiMessage]);

      return response;

    } catch (error) {
      // Add error message to chat
      const errorMessage: ChatMessage = {
        id: this.generateMessageId(),
        role: 'assistant',
        answer: userFacingChatError(CHAT_ERROR_MESSAGES.generic),
        timestamp: new Date(),
        metadata: {
          confidence: 0,
          modelUsed: 'error'
        }
      };
      
      // Add error message to chat history
      const currentHistory = this.chatHistory();
      this.chatHistory.set([...currentHistory, errorMessage]);
      
      throw error;
    } finally {
      this.isLoadingSubject.next(false);
    }
  }

  async sendMessageStream(
    message: string, 
    userPreferences?: UserPreferences,
    onChunk?: (chunk: string) => void,
    onComplete?: (sources: any[]) => void,
    onError?: (error: string) => void,
    quizActionOverride?: QuizAction
  ): Promise<void> {
    let aiMessageId: string | null = null;

    try {
      this.isLoadingSubject.next(true);
      this.isProcessing.set(true);

      const isQuiz = this.isQuizMode();
      const quizAction: QuizAction | undefined = isQuiz
        ? quizActionOverride ||
          (this.quizState().questionNumber === 0 && !this.quizState().roundComplete
            ? this.chatHistory().length === 0
              ? 'start'
              : 'answer'
            : this.quizState().roundComplete
              ? 'new_round'
              : 'answer')
        : undefined;

      // Hide synthetic start prompts from the transcript; show real student answers.
      const hideUserBubble =
        isQuiz && (quizAction === 'start' || quizAction === 'new_round');

      const messageId = this.generateMessageId();
      if (!hideUserBubble) {
        const userMessage: ChatMessage = {
          id: messageId,
          role: 'user',
          question: message,
          timestamp: new Date()
        };
        this.chatHistory.update(history => [...history, userMessage]);
      }

      // Show the assistant "Thinking..." placeholder instantly so the UX matches the
      // original behaviour. Turnstile + network work happen afterwards, behind it.
      aiMessageId = this.generateMessageId();
      const aiMessage: ChatMessage = {
        id: aiMessageId,
        role: 'assistant',
        answer: 'Thinking...',
        timestamp: new Date(),
        isProcessing: true
      };

      this.chatHistory.update(history => [...history, aiMessage]);

      dispatchChatExchangeStarted();

      const payload = await this.buildChatPayload(
        message,
        userPreferences,
        isQuiz ? null : this.currentConversationId,
        quizAction
      );

      const authToken = this.authHelper.getToken();
      
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json'
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await this.postChatStream(payload, headers);

      if (!response.ok) {
        throw new Error(await this.getErrorMessageFromResponse(response));
      }

      if (!response.body) {
        throw new Error(userFacingChatError(CHAT_ERROR_MESSAGES.responseBodyNull));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullAnswer = '';
      let sseBuffer = '';
      let serverError: string | null = null;
      let idleTimedOut = false;
      const streamIdleTimeoutMs = 120_000;
      let streamIdleTimer: ReturnType<typeof setTimeout> | null = null;

      const resetStreamIdleTimer = (): void => {
        if (streamIdleTimer) {
          clearTimeout(streamIdleTimer);
        }
        streamIdleTimer = setTimeout(() => {
          idleTimedOut = true;
          void reader.cancel('stream-idle-timeout');
        }, streamIdleTimeoutMs);
      };

      const clearStreamIdleTimer = (): void => {
        if (streamIdleTimer) {
          clearTimeout(streamIdleTimer);
          streamIdleTimer = null;
        }
      };

      try {
        resetStreamIdleTimer();
        streamLoop: while (true) {
          const { done, value } = await reader.read();
          if (done) {
            sseBuffer += decoder.decode();
          } else if (value) {
            resetStreamIdleTimer();
            sseBuffer += decoder.decode(value, { stream: true });
          }

          const parts = sseBuffer.split('\n');
          sseBuffer = parts.pop() ?? '';

          for (const rawLine of parts) {
            const line = rawLine.replace(/\r$/, '');
            if (!line.startsWith('data: ')) {
              continue;
            }
            const data = line.slice(6); // Remove 'data: ' prefix

            try {
              const parsed = JSON.parse(data);

              if (parsed.type === 'content' && parsed.content) {
                // Quiz mode: keep "Thinking..." until structured answer_final cards arrive.
                // Do not paint raw model stream text into the quiz bubbles.
                if (this.isQuizMode()) {
                  continue;
                }
                if (parsed.contentMode === 'replace') {
                  fullAnswer = parsed.content;
                  this.chatHistory.update(history => {
                    const updatedHistory = history.map(msg =>
                      msg.id === aiMessageId!
                        ? { ...msg, answer: fullAnswer, isProcessing: true }
                        : msg
                    );
                    return updatedHistory;
                  });
                  this.chatHistory.set([...this.chatHistory()]);
                  if (onChunk) {
                    onChunk(parsed.content);
                  }
                  await this.yieldForPaint();
                } else {
                  fullAnswer = await this.appendAssistantStreamingDelta(
                    aiMessageId!,
                    parsed.content,
                    fullAnswer,
                    onChunk
                  );
                }
              } else if (parsed.type === 'status') {
                // Server "started" heartbeat — confirms the request was received.
                // No display change; the "Thinking..." placeholder is already shown.
              // Crisis banner disabled pending leadership approval
              // } else if (parsed.type === 'crisis') {
              //   this.activateCrisisBanner(parsed);
              } else if (parsed.type === 'answer_final' && parsed.content) {
                // Server sends fully formatted HTML after NASB enrichment (replaces provisional streamed text)
                fullAnswer = parsed.content;
                this.chatHistory.update(history => {
                  const updatedHistory = history.map(msg =>
                    msg.id === aiMessageId!
                      ? { ...msg, answer: fullAnswer, isProcessing: true }
                      : msg
                  );
                  return updatedHistory;
                });
                this.chatHistory.set([...this.chatHistory()]);
              } else if (parsed.type === 'title') {
                  // Handle conversation title event - emit for conversation service to pick up
                  if (parsed.title && parsed.conversationId) {
                    // Set the conversationId so subsequent messages use the same conversation
                    this.setConversationId(parsed.conversationId);
                    
                    // Emit custom event for conversation service to listen to
                    window.dispatchEvent(new CustomEvent('conversation-title-generated', {
                      detail: {
                        conversationId: parsed.conversationId,
                        title: parsed.title
                      }
                    }));
                  }
                } else if (parsed.type === 'complete') {
                  // Update with sources, follow-up questions, chatId, and mark as complete
                  const segments = parsed.relevantSegments || [];
                  const ctxTotal =
                    typeof parsed.contextSegmentCount === 'number'
                      ? parsed.contextSegmentCount
                      : undefined;
                  const hasMore =
                    typeof ctxTotal === 'number'
                      ? ctxTotal > segments.length
                      : false;
                  const quizDetails: QuizMessageDetails | undefined = parsed.quiz
                    ? {
                        ...parsed.quiz,
                        expanded: { feedback: false, verses: false, sources: false },
                      }
                    : undefined;
                  if (quizDetails) {
                    this.applyQuizCompleteMeta(quizDetails);
                  }

                  const splitNext =
                    !!quizDetails?.nextQuestionHtml &&
                    !!quizDetails?.grade &&
                    !quizDetails?.roundComplete;

                  const gradeQuizDetails: QuizMessageDetails | undefined = quizDetails
                    ? {
                        ...quizDetails,
                        isQuestionCard: false,
                        // Keep grading controls on the grade bubble only
                        splitNextQuestion: false,
                        nextQuestionHtml: null,
                      }
                    : undefined;

                  this.chatHistory.update(history => 
                    history.map(msg => 
                      msg.id === aiMessageId! 
                        ? { 
                            ...msg, 
                            // fullAnswer should already be answer_final grade-only HTML from server
                            answer: fullAnswer, 
                            isProcessing: false,
                            sources: segments,
                            followupQuestions: this.isQuizMode()
                              ? []
                              : parsed.followUpQuestions || [],
                            chatId: parsed.chatId, // Include chat ID for feedback
                            contextSegmentCount: ctxTotal,
                            hasMoreSources: this.isQuizMode() ? false : hasMore,
                            quiz: gradeQuizDetails,
                          }
                        : msg
                    )
                  );

                  // Separate next-question bubble after the grade/feedback bubble
                  if (splitNext && quizDetails?.nextQuestionHtml) {
                    const nextQuestionMessage: ChatMessage = {
                      id: this.generateMessageId(),
                      role: 'assistant',
                      answer: quizDetails.nextQuestionHtml,
                      timestamp: new Date(),
                      isProcessing: false,
                      sources: [],
                      followupQuestions: [],
                      quiz: {
                        phase: 'question',
                        questionNumber: quizDetails.questionNumber,
                        totalQuestions: quizDetails.totalQuestions,
                        correct: quizDetails.correct,
                        partial: quizDetails.partial || 0,
                        incorrect: quizDetails.incorrect,
                        grade: null,
                        question: quizDetails.question,
                        topic: quizDetails.topic,
                        roundComplete: false,
                        isQuestionCard: true,
                        expanded: { feedback: false, verses: false, sources: false },
                      },
                    };
                    this.chatHistory.update((history) => [...history, nextQuestionMessage]);
                  }
                  
                  // Set the conversationId if we receive it (for new conversations, this ensures subsequent messages use the same ID)
                  // Quiz mode is session-only — never adopt a conversationId from quiz turns.
                  if (parsed.conversationId && !this.isQuizMode()) {
                    this.setConversationId(parsed.conversationId);
                  }
                  
                  // Handle conversation title from complete event (fallback if title event wasn't sent)
                  if (parsed.conversationTitle && parsed.conversationId) {
                    window.dispatchEvent(new CustomEvent('conversation-title-generated', {
                      detail: {
                        conversationId: parsed.conversationId,
                        title: parsed.conversationTitle
                      }
                    }));
                  }
                  
                  // If this is an existing conversation (not a new one), notify that it was updated
                  // This will trigger a refresh of the conversation list to update message count and move it to top
                  // Note: We check if currentConversationId was already set (meaning it's an existing conversation)
                  // If it wasn't set before, this is a new conversation and we just set it above
                  if (this.currentConversationId && parsed.conversationId) {
                    // Check if this conversationId was different before (meaning we're continuing an old conversation)
                    // We need to compare with the previous value, but since we just set it, we'll use a different approach
                    // Actually, if currentConversationId was null before and we just set it, this is a new conversation
                    // So we should only dispatch the update event if the conversationId was already set before this message
                    // For now, let's always dispatch if we have a conversationId - the backend query will handle it correctly
                    window.dispatchEvent(new CustomEvent('conversation-updated', {
                      detail: {
                        conversationId: parsed.conversationId
                      }
                    }));
                  }
                  
                  // Call the onComplete callback
                  if (onComplete) {
                    onComplete(parsed.relevantSegments || []);
                  }
                  
                  // Update lastTopSegments
                  this.lastTopSegments.set(parsed.relevantSegments || []);

                  break streamLoop;
                } else if (parsed.type === 'error') {
                  // Capture (don't throw here) so the local catch can't swallow it.
                  serverError = parsed.error || CHAT_ERROR_MESSAGES.generic;
                }
              } catch {
                // Incomplete JSON across chunks is fixed by line buffering; other parse errors ignored.
              }
          }

          if (serverError || done) {
            break;
          }
        }
      } finally {
        clearStreamIdleTimer();
        reader.releaseLock();
      }

      // Server explicitly reported a failure — surface its actual reason.
      if (serverError) {
        throw new Error(serverError);
      }

      // The stream stalled mid-flight with nothing usable — this is the genuine
      // "stuck" case the hardening targets, so report it as a timeout.
      if (idleTimedOut && !fullAnswer) {
        throw new Error(CHAT_ERROR_MESSAGES.streamTimedOut);
      }

      // Stream ended without an explicit "complete" event. Keep whatever streamed in;
      // only fall back to a neutral message when nothing arrived at all.
      if (aiMessageId) {
        this.chatHistory.update(history =>
          history.map(msg =>
            msg.id === aiMessageId && msg.isProcessing
              ? {
                  ...msg,
                  answer:
                    fullAnswer ||
                    userFacingChatError(CHAT_ERROR_MESSAGES.generic),
                  isProcessing: false,
                }
              : msg
          )
        );
      }

    } catch (error) {
      const rawMessage =
        error instanceof DOMException && error.name === 'AbortError'
          ? CHAT_ERROR_MESSAGES.streamTimedOut
          : error instanceof Error && error.message
            ? error.message
            : CHAT_ERROR_MESSAGES.generic;
      const isInternalFetchError = /body is unusable|already been read|failed to fetch|networkerror|econnreset/i.test(rawMessage);
      const errorText = userFacingChatError(isInternalFetchError ? CHAT_ERROR_MESSAGES.generic : rawMessage);

      if (aiMessageId) {
        this.chatHistory.update(history =>
          history.map(msg =>
            aiMessageId && msg.id === aiMessageId
              ? { ...msg, answer: errorText, isProcessing: false }
              : msg
          )
        );
      } else {
        this.chatHistory.update(history => [
          ...history,
          {
            id: this.generateMessageId(),
            role: 'assistant',
            answer: errorText,
            timestamp: new Date(),
            isProcessing: false,
          },
        ]);
      }

      if (onError) {
        onError(errorText);
      }
    } finally {
      this.isLoadingSubject.next(false);
      this.isProcessing.set(false);
    }
  }

  // Add streaming chat method for complete responses
  async sendMessageStreaming(message: string, userPreferences?: UserPreferences): Promise<Observable<string>> {
    this.isProcessing.set(true);
    
    const currentMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      question: message,
      timestamp: new Date(),
      isProcessing: true
    };

    // Add user message to chat history
    this.chatHistory.update(history => [...history, currentMessage]);

    // Create AI response message
    const aiMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      answer: '',
      timestamp: new Date(),
      isProcessing: true
    };

    // Add AI message to chat history
    this.chatHistory.update(history => [...history, aiMessage]);

    return new Observable<string>(observer => {
      const eventSource = new EventSource(`${this.apiUrl}/chat-stream?message=${encodeURIComponent(message)}&sessionId=${this.sessionId}&userPreferences=${encodeURIComponent(JSON.stringify(userPreferences || {}))}`);

      let fullResponse = '';

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'content') {
            fullResponse += data.content;
            // Update the AI message with accumulated content
            this.chatHistory.update(history => 
              history.map(msg => 
                msg.id === aiMessage.id 
                  ? { ...msg, answer: fullResponse, isProcessing: true }
                  : msg
              )
            );
            observer.next(data.content);
          } else if (data.type === 'complete') {
            // Finalize the AI message
            this.chatHistory.update(history => 
              history.map(msg => 
                msg.id === aiMessage.id 
                  ? { 
                      ...msg, 
                      answer: fullResponse, 
                      isProcessing: false,
                      sources: data.relevantSegments,
                      followupQuestions: data.followUpQuestions
                    }
                  : msg
              )
            );
            
                                  // Update last top segments
                      if (data.relevantSegments) {
                        this.lastTopSegments.set(data.relevantSegments);
                      }
            
            eventSource.close();
            observer.complete();
            this.isProcessing.set(false);
          } else if (data.type === 'error') {
            eventSource.close();
            observer.error(new Error(data.error));
            this.isProcessing.set(false);
          }
        } catch (error) {
          eventSource.close();
          observer.error(error);
          this.isProcessing.set(false);
        }
      };

      eventSource.onerror = (error) => {
        eventSource.close();
        observer.error(error);
        this.isProcessing.set(false);
      };

      // Return cleanup function
      return () => {
        eventSource.close();
      };
    });
  }

  async getConversationStarters(): Promise<string[]> {
    try {
      const response = await this.http.get<any>(`${this.apiUrl}/prompt-settings`).toPromise();
      return response?.conversationStarters || [];
    } catch (error) {
      // Return fallback starters
      return [
        "What are the key principles of Christian living?",
        "How can I deepen my understanding of Scripture?",
        "What does the Bible say about prayer and meditation?",
        "Tell me about faith and how it's taught in the Bible",
        "What are the practical applications of biblical teachings?"
      ];
    }
  }

  async getChatHistory(): Promise<ChatMessage[]> {
    try {
      // Backend doesn't support chat history persistence yet
      // Return empty array for now
      return [];
    } catch (error) {
      return [];
    }
  }

  async clearChat(preserveConversationId: boolean = false): Promise<void> {
    try {
      // Clear local chat history immediately
      this.chatHistory.set([]);
      this.resetCrisisBanner();
      // Leaving a conversation (or starting fresh) always exits quiz mini-game mode
      this.conversationMode.set('chat');
      this.quizState.set(ChatService.initialQuizState());
      
      // Try to clear backend context if endpoint exists (optional)
      try {
        await this.http.post(`${this.apiUrl}/clear-chat`, { sessionId: this.sessionId }).toPromise();
      } catch (backendError) {
        // Backend endpoint might not exist, which is fine
      }
      
      // Generate new session ID for fresh conversation
      this.sessionId = this.generateSessionId();
      this.persistSessionId(this.sessionId);
      
      // Clear current conversationId when starting a new conversation, unless we're preserving it (e.g., loading an old conversation)
      if (!preserveConversationId) {
        this.setConversationId(null);
      }
      
      // Reset conversation state to show suggested prompts again
      this.resetConversationState();
      
    } catch (error) {
      // Even if there's an error, ensure local history is cleared
      this.chatHistory.set([]);
      this.conversationMode.set('chat');
      this.quizState.set(ChatService.initialQuizState());
      this.resetConversationState();
    }
  }

  // Method to reset conversation state and show suggested prompts
  public resetConversationState(): void {
    // This method can be called directly by components to reset their state
    // Components should listen to chatHistory changes directly
    this.resetCrisisBanner();
  }

  public activateCrisisBanner(payload: { level?: string; resources?: CrisisResources }): void {
    this.crisisBannerActive.set(true);
    if (payload.level) {
      this.crisisLevel.set(payload.level);
    }
    if (payload.resources) {
      this.crisisResources.set(payload.resources);
    }
  }

  public resetCrisisBanner(): void {
    this.crisisBannerActive.set(false);
    this.crisisResources.set(null);
    this.crisisLevel.set(null);
  }

  /** Whether “Show more” should appear under Sources & References */
  showMoreSourcesButton(message: ChatMessage): boolean {
    if (!message.chatId || message.sources === undefined) return false;
    if (message.hasMoreSources === false) return false;
    if (message.hasMoreSources === true) return true;
    const total = message.contextSegmentCount ?? 0;
    const shown = message.sources?.length ?? 0;
    return total > shown;
  }

  /** Append next sources from ChatLogs.ContextSegmentIds (by score, excluding ids already shown). */
  async loadMoreSources(message: ChatMessage): Promise<void> {
    if (!message.chatId || !message.sources?.length) return;
    this.loadingMoreSourcesChatId.set(message.chatId);
    try {
      const authToken = this.authHelper.getToken();
      const headers: { [key: string]: string } = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      const excludeIds = message.sources.map((s: any) => s?.id).filter(Boolean) as string[];
      const res = await firstValueFrom(
        this.http.post<{ sources: any[]; hasMore: boolean }>(
          `${this.apiUrl}/chat/more-sources`,
          { chatId: message.chatId, excludeIds },
          { headers }
        )
      );
      const merged = [...message.sources, ...(res.sources || [])];
      this.chatHistory.update((history) =>
        history.map((m) =>
          m.id === message.id ? { ...m, sources: merged, hasMoreSources: res.hasMore } : m
        )
      );
    } catch (e) {
      console.error('[ChatService] loadMoreSources failed:', e);
    } finally {
      this.loadingMoreSourcesChatId.set(null);
    }
  }

  private generateMessageId(): string {
    return 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /** Lets the browser paint between streamed chunks so the answer does not appear in one frame. */
  private yieldForPaint(): Promise<void> {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  }

  /**
   * Large model deltas can arrive in one SSE event; split into smaller UI steps so typing feels gradual.
   */
  private async appendAssistantStreamingDelta(
    aiMessageId: string,
    piece: string,
    priorFull: string,
    onChunk?: (chunk: string) => void
  ): Promise<string> {
    const step = 36;
    let acc = priorFull;
    const slices =
      piece.length <= step
        ? [piece]
        : Array.from({ length: Math.ceil(piece.length / step) }, (_, i) =>
            piece.slice(i * step, i * step + step));

    for (const slice of slices) {
      acc += slice;
      this.chatHistory.update(history => {
        const updatedHistory = history.map(msg =>
          msg.id === aiMessageId ? { ...msg, answer: acc, isProcessing: true } : msg
        );
        return updatedHistory;
      });
      this.chatHistory.set([...this.chatHistory()]);
      if (onChunk) {
        onChunk(slice);
      }
      await this.yieldForPaint();
    }
    return acc;
  }

  private async loadChatHistory(): Promise<void> {
    try {
      const history = await this.getChatHistory();
      if (history.length > 0) {
        this.chatHistory.set(history);
      }
    } catch (error) {
      // Error silently handled
    }
  }

  private async loadConversationStarters(): Promise<void> {
    try {
      // First try to get from prompt settings
      const promptSettings = this.promptSettings();
      if (promptSettings && promptSettings.conversationStarters.length > 0) {
        this.conversationStarters.set(promptSettings.conversationStarters);
        return;
      }

      // Fallback to hardcoded starters
      const starters = [
        "What are the key principles of Christian living?",
        "How can I deepen my understanding of Scripture?",
        "What does the Bible say about prayer and meditation?"
      ];
      this.conversationStarters.set(starters);
    } catch (error) {
      // Set fallback conversation starters
      const fallbackStarters = [
        "What are the key principles of Christian living?",
        "How can I deepen my understanding of Scripture?",
        "What does the Bible say about prayer and meditation?"
      ];
      this.conversationStarters.set(fallbackStarters);
    }
  }

  private async loadPromptSettings(): Promise<void> {
    try {
      const response = await this.http.get<any>(`${this.apiUrl}/prompt-settings`).toPromise();
      
      if (response) {
        this.promptSettings.set(response);
        this.conversationStarters.set(response.conversationStarters);
        this.chatAvatars.set(response.avatars);
      }
    } catch (error) {
      // Set fallback conversation starters
      const fallbackStarters = [
        "💡 What are the key principles of Christian living?",
        "💡 How can I deepen my understanding of Scripture?",
        "💡 What does the Bible say about prayer and meditation?"
      ];
      this.conversationStarters.set(fallbackStarters);
    }
  }

  // Method to refresh prompt settings (useful for admin updates)
  async refreshPromptSettings(): Promise<void> {
    await this.loadPromptSettings();
  }

  // Method to manually set conversation starters for testing
  setConversationStarters(starters: string[]): void {
    this.conversationStarters.set(starters);
  }

  // Method to update feedback for a specific chat message
  updateMessageFeedback(chatId: number, feedback: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/chat/feedback`, { chatId, feedback });
  }

  // Track content engagement (video/document views)
  trackContentEngagement(chatId: number, contentType: 'video' | 'document', contentTitle: string, action: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/content-engagement`, { 
      chatId, 
      contentType, 
      contentTitle, 
      action 
    });
  }

  // Debug method to check current state
  debugPromptSettings(): void {
    // Debug method - no logging in production
  }

  // Debug method to test prompt settings endpoint directly
  async testPromptSettingsEndpoint(): Promise<void> {
    try {
      const response = await this.http.get<any>(`${this.apiUrl}/prompt-settings`).toPromise();
      // Test completed - no logging in production
    } catch (error) {
      // Error silently handled
    }
  }

  private getUserId(): string {
    // In a real app, this would come from authentication service
    return localStorage.getItem('userId') || 'anonymous';
  }

  private generateRequestId(): string {
    return 'req-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  private async buildChatPayload(
    message: string,
    userPreferences?: UserPreferences,
    conversationId?: string | null,
    quizAction?: QuizAction
  ): Promise<Record<string, unknown>> {
    const turnstileResult = await this.turnstileService.getTokenForChat();
    const payload: Record<string, unknown> = {
      message,
      sessionId: this.sessionId,
      userId: this.getUserId(),
      userPreferences,
      requestId: this.generateRequestId(),
    };

    if (conversationId) {
      payload['conversationId'] = conversationId;
    }

    if (this.isQuizMode()) {
      const qs = this.quizState();
      payload['mode'] = 'quiz';
      payload['quizAction'] =
        quizAction ||
        (qs.questionNumber === 0 ? 'start' : qs.roundComplete ? 'new_round' : 'answer');
      payload['quizState'] = {
        questionNumber: qs.questionNumber,
        correct: qs.correct,
        partial: qs.partial,
        incorrect: qs.incorrect,
        lastQuestion: qs.lastQuestion,
        lastTopic: qs.lastTopic,
        previousTopics: qs.previousTopics,
        totalQuestions: qs.totalQuestions,
      };
    }

    if (turnstileResult?.token) {
      payload['turnstileToken'] = turnstileResult.token;
      if (turnstileResult.keyHint) {
        payload['turnstileKeyHint'] = turnstileResult.keyHint;
      }
    }
    payload['clientContext'] = detectWebViewContext();
    payload['turnstileDiagnostic'] = this.turnstileService.getDiagnosticSummary();

    return payload;
  }

  private async postChatStream(
    payload: Record<string, unknown>,
    headers: Record<string, string>,
    options?: { allowTurnstileRetry?: boolean }
  ): Promise<Response> {
    const allowTurnstileRetry = options?.allowTurnstileRetry !== false;
    let currentPayload = payload;
    const requestTimeoutMs = 180_000;
    let turnstileRetries = 0;
    const maxTurnstileRetries = 2;

    for (let attempt = 0; attempt < 4; attempt++) {
      const response = await this.fetchWithTimeout(
        `${this.apiUrl}/chat-stream`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(currentPayload),
        },
        requestTimeoutMs
      );

      if (response.ok) {
        return response;
      }

      const errorInfo = await this.parseErrorResponse(response);

      if (
        allowTurnstileRetry &&
        turnstileRetries < maxTurnstileRetries &&
        (response.status === 403 || response.status === 503) &&
        this.isTurnstileErrorCode(errorInfo.code)
      ) {
        if (errorInfo.code === 'TURNSTILE_REQUIRED' && this.authHelper.isAuthenticated()) {
          this.authHelper.clearAuth();
        }

        // Server rejected the token. Always escalate to a fresh VISIBLE (managed) challenge on
        // TURNSTILE_FAILED — invisible retries after a managed/iPhone failure caused invalid-
        // input-response loops. Clear any stale turnstileKeyHint so the next verify uses the
        // secret that matches the new token.
        const forceInteractive =
          errorInfo.code === 'TURNSTILE_FAILED' ||
          errorInfo.code === 'TURNSTILE_REQUIRED' ||
          turnstileRetries >= 1;
        turnstileRetries++;

        const freshResult = await this.turnstileService.getTokenForChat({
          forceFresh: true,
          forceInteractive,
        });
        if (freshResult?.token) {
          const nextPayload: Record<string, unknown> = {
            ...currentPayload,
            turnstileToken: freshResult.token,
          };
          delete nextPayload['turnstileKeyHint'];
          if (freshResult.keyHint) {
            nextPayload['turnstileKeyHint'] = freshResult.keyHint;
          }
          currentPayload = nextPayload;
          continue;
        }
      }

      if (response.status === 503 && attempt < 3) {
        await new Promise((resolve) => window.setTimeout(resolve, (attempt + 1) * 1000));
        continue;
      }

      return response;
    }

    return this.fetchWithTimeout(
      `${this.apiUrl}/chat-stream`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(currentPayload),
      },
      requestTimeoutMs
    );
  }

  private async fetchWithTimeout(
    url: string,
    init: RequestInit,
    timeoutMs: number
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, { ...init, signal: controller.signal });
      window.clearTimeout(timeoutId);
      return response;
    } catch (error) {
      window.clearTimeout(timeoutId);
      throw error;
    }
  }

  private isTurnstileErrorCode(code?: string): boolean {
    return code === 'TURNSTILE_REQUIRED' || code === 'TURNSTILE_FAILED' || code === 'TURNSTILE_UNAVAILABLE';
  }

  private async parseErrorResponse(response: Response): Promise<{ message: string; code?: string }> {
    try {
      const body = await response.clone().json();
      const code = typeof body?.code === 'string' ? body.code : undefined;
      if (body?.message) {
        return { message: body.message, code };
      }
      if (body?.error) {
        return {
          message: typeof body.error === 'string' ? body.error : 'Request failed',
          code,
        };
      }
      return { message: this.defaultErrorMessage(response.status), code };
    } catch {
      return { message: this.defaultErrorMessage(response.status) };
    }
  }

  private defaultErrorMessage(status: number): string {
    if (status === 429) {
      return CHAT_ERROR_MESSAGES.rateLimited;
    }
    if (status === 403) {
      return CHAT_ERROR_MESSAGES.turnstileFailed;
    }
    if (status === 503) {
      return CHAT_ERROR_MESSAGES.turnstileUnavailable;
    }
    return CHAT_ERROR_MESSAGES.requestFailed(status);
  }

  private async getErrorMessageFromResponse(response: Response): Promise<string> {
    const errorInfo = await this.parseErrorResponse(response);
    return userFacingChatError(errorInfo.message);
  }

  getCurrentSessionId(): string {
    return this.sessionId;
  }
  
  // Set the conversationId when resuming an old conversation
  setConversationId(conversationId: string | null): void {
    this.currentConversationId = conversationId;
    try {
      if (conversationId) {
        sessionStorage.setItem(ChatService.CONVERSATION_STORAGE_KEY, conversationId);
      } else {
        sessionStorage.removeItem(ChatService.CONVERSATION_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }
  
  // Get the current conversationId
  getConversationId(): string | null {
    return this.currentConversationId;
  }

  getMessages(): ChatMessage[] {
    return this.chatHistory();
  }

  // Method to handle video uploads
  async uploadVideo(videoUrl: string, videoName: string, description?: string): Promise<any> {
    try {
      const payload = {
        videoUrl,
        videoName,
        description,
        userId: this.getUserId()
      };

      const response = await this.http.post(`${this.apiUrl}/video-upload`, payload).toPromise();
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Method to get video processing status
  async getVideoStatus(videoId: string): Promise<any> {
    try {
      const response = await this.http.get(`${this.apiUrl}/video-status?videoId=${videoId}`).toPromise();
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Method to search for specific content
  async searchContent(query: string, filters?: any): Promise<VideoSegment[]> {
    try {
      // This would call a search endpoint if implemented
      // For now, we'll use the chat endpoint with a search-like query
      const response = await this.sendMessage(`Search for: ${query}`);
      return response.sources || [];
    } catch (error) {
      return [];
    }
  }

  // Method to get popular topics
  async getPopularTopics(): Promise<string[]> {
    try {
      // This would call a topics endpoint if implemented
      // For now, return some common topics
      return [
        'faith',
        'prayer',
        'bible study',
        'worship',
        'discipleship',
        'forgiveness',
        'love',
        'hope',
        'grace',
        'salvation'
      ];
    } catch (error) {
      return [];
    }
  }
}
